import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'packages';
import { toSearchString } from 'utils/url';
import { BPOINT, breakPoints } from 'constants/breakpoints';
import uiActions from 'state/actions/ui';

import storageUtil from 'utils/storage';
import STRINGS from 'constants/strings';
import Bookmark from '@tradera/tradera-ui/icons/bookmark.svg?jsx';
import CloseIcon from '@tradera/tradera-ui/icons/close-narrow.svg?jsx';
import BulbIcon from '@tradera/tradera-ui/icons/bulb.svg?jsx';
import InfoIcon from '@tradera/tradera-ui/icons/info.svg?jsx';
import * as gtm from 'components/search-results/search-gtm';
import './save-search-button.scss';

const LOCAL_STORAGE_TOOLTIP_KEY = 'search-result-tooltip';
const LOCAL_STORAGE_CLICKED_VALUE = 'CLOSE_CLICKED';

const GA_CATEGORY = 'Saved Search';

const isDesktopWidth = () => window.innerWidth > breakPoints[BPOINT.small].max; //TODO - make util
const localStorage = storageUtil.getLocalStorage();

class SaveSearchButton extends PureComponent {
    static propTypes = {
        queryParams: PropTypes.object.isRequired,
        dispatch: PropTypes.func.isRequired,
        dataService: PropTypes.shape({
            saveSearch: PropTypes.func.isRequired,
            removeSavedSearch: PropTypes.func.isRequired,
            saveEmailNotificationEnabled: PropTypes.func.isRequired
        }).isRequired,
        category: PropTypes.object.isRequired,
        searchName: PropTypes.string.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            showSavedTooltip: false,
            showHintTooltip:
                localStorage &&
                localStorage.getItem(LOCAL_STORAGE_TOOLTIP_KEY) !==
                    LOCAL_STORAGE_CLICKED_VALUE,
            categoryId: props.queryParams.categoryId,
            emailNotificationEnabled: false,
            savedSearchId: 0,
            searchTerm: null,
            inProgress: false
        };
        const debounceTimeout = 600;
        this.debouncedSaveSearch = debounce(
            this.debouncedSaveSearch,
            debounceTimeout
        );
        this.debouncedRemoveSaved = debounce(
            this.debouncedRemoveSaved,
            debounceTimeout
        );
        this.debouncedNotificationChange = debounce(
            this.debouncedNotificationChange,
            debounceTimeout
        );
    }

    componentWillReceiveProps(nextProps) {
        const categoryId = nextProps.category.categoryId;
        const searchTerm = nextProps.queryParams.q;
        if (
            categoryId !== this.props.category.categoryId ||
            searchTerm !== this.state.searchTerm
        ) {
            this.setState({ categoryId, searchTerm, savedSearchId: 0 });
        }
    }

    componentDidMount() {
        const { queryParams } = this.props;
        if (queryParams.saveSearch) {
            this.saveSearch(queryParams.q);
        }
    }

    handleCloseTooltip = () => {
        storageUtil
            .getLocalStorage()
            .setItem(LOCAL_STORAGE_TOOLTIP_KEY, LOCAL_STORAGE_CLICKED_VALUE);
        this.setState({ showHintTooltip: false });
        gtm.trackAction(`Tooltip: ${GA_CATEGORY}`, 'Closed');
    };

    handleCloseSavedTooltip = () => {
        this.setState({ showSavedTooltip: false });
    };

    async toggleEmailNotificationEnabled() {
        const { savedSearchId, emailNotificationEnabled } = this.state;
        if (savedSearchId < 1) return;
        const enabled = !emailNotificationEnabled;
        this.setState({ emailNotificationEnabled: enabled });
        this.debouncedNotificationChange(
            savedSearchId,
            enabled,
            requestedEnableStatus => {
                this.setState({
                    emailNotificationEnabled: !requestedEnableStatus
                });
            }
        );
        gtm.trackAction(GA_CATEGORY, enabled ? 'Email removed' : 'Email added');
    }

    async debouncedNotificationChange(savedSearchId, enabled, onError) {
        const responseData = await this.props.dataService.saveEmailNotificationEnabled(
            savedSearchId,
            enabled
        );
        if (!responseData || responseData.error) {
            onError(enabled);
        }
    }

    handleLogin = () => {
        const { dispatch, queryParams } = this.props;
        const query = {
            ...queryParams,
            saveSearch: 'true'
        };
        const redirectUrl = encodeURIComponent(
            `${window.location.pathname}${toSearchString(query)}`
        );
        dispatch(uiActions.signIn({ redirectUrl }));
    };

    async saveSearch(searchTerm) {
        const { isLoggedIn, category, searchName, queryParams } = this.props;

        if (!isLoggedIn) {
            this.handleLogin();
            return;
        }

        this.setState({
            showSavedTooltip: true,
            showHintTooltip: false,
            emailNotificationEnabled: true,
            inProgress: true
        });

        this.debouncedSaveSearch(
            queryParams.q ? searchTerm : searchName,
            category.categoryId,
            searchTerm,
            savedSearchId => {
                this.setState({ savedSearchId, inProgress: false });
            },
            () => {
                this.setState({ showSavedTooltip: false, inProgress: false });
            }
        );

        gtm.trackAction('Saved Search', 'Saved');
    }

    async debouncedSaveSearch(
        savedSearchName,
        categoryId,
        searchTerm,
        onSuccess,
        onError
    ) {
        const responseData = await this.props.dataService.saveSearch(
            savedSearchName,
            categoryId,
            searchTerm
        );
        if (!responseData || responseData.error) {
            onError();
        } else {
            const id =
                (responseData.ids && responseData.ids[0]) || responseData.id;
            onSuccess(id);
        }
    }

    async removeSaved(savedSearchId) {
        const { isLoggedIn } = this.props;

        if (!isLoggedIn) {
            this.handleLogin();
            return;
        }

        if (savedSearchId < 1) return;

        const savedTooltipWasVisible = this.state.showSavedTooltip;

        this.setState({
            showSavedTooltip: false,
            inProgress: true
        });

        this.debouncedRemoveSaved(
            savedSearchId,
            () => this.setState({ savedSearchId: 0, inProgress: false }),
            () => {
                this.setState({
                    savedSearchId,
                    showSavedTooltip: savedTooltipWasVisible,
                    inProgress: false
                });
            }
        );

        gtm.trackAction('Saved Search', 'Removed');
    }

    async debouncedRemoveSaved(savedSearchId, onSuccess, onError) {
        const responseData = await this.props.dataService.removeSavedSearch(
            savedSearchId
        );
        if (!responseData || responseData.error) {
            onError();
        } else {
            onSuccess();
        }
    }

    handleSaveSearchClick = () => {
        const { savedSearchId, searchTerm } = this.state;

        if (savedSearchId > 0) {
            this.removeSaved(savedSearchId);
        } else {
            this.saveSearch(searchTerm);
        }
    };

    render() {
        const {
            showSavedTooltip,
            showHintTooltip,
            savedSearchId,
            searchTerm,
            emailNotificationEnabled,
            inProgress
        } = this.state;
        const isSaved =
            (savedSearchId > 0 && !inProgress) ||
            (savedSearchId === 0 && inProgress);
        const shouldShowSavedTooltip = showSavedTooltip && isSaved;
        return (
            <div
                className={`tooltip_container tooltip-toggle ${
                    showSavedTooltip || showHintTooltip
                        ? 'tooltip-toggle-show'
                        : ''
                } flex-md-grow-1 mr-md-3`}>
                <button
                    type="button"
                    className={`btn btn-sm btn-toggle w-100 ${
                        isSaved ? 'active' : ''
                    }`}
                    data-test-save-search-button
                    onClick={() => this.handleSaveSearchClick()}>
                    <Bookmark className="icon icon-before" />
                    {isSaved
                        ? STRINGS.DISCOVER.SAVE_SEARCH_SAVED
                        : STRINGS.DISCOVER.SAVE_SEARCH}
                </button>
                {shouldShowSavedTooltip ? (
                    <span
                        className={`tooltip ${isDesktopWidth() &&
                            'tooltip-southwest'}`}>
                        <InfoIcon className="tooltip_icon icon" />
                        <span className="tooltip_content">
                            <span className="text-nowrap">
                                Din bevakning av <strong>{searchTerm}</strong>{' '}
                                är nu skapad.
                            </span>
                            <div className="d-flex align-items-baseline">
                                <input
                                    type="checkbox"
                                    id="mail-notification"
                                    name="mail-notification"
                                    onChange={() => {
                                        this.toggleEmailNotificationEnabled();
                                    }}
                                    checked={emailNotificationEnabled}
                                />
                                <label
                                    htmlFor="mail-notification"
                                    className="ml-2 text-nowrap">
                                    {STRINGS.DISCOVER.SAVE_SEARCH_NOTIFICATION}
                                </label>
                            </div>
                        </span>
                        <span
                            className="tooltip_close-icon"
                            onClick={() => this.handleCloseSavedTooltip()}>
                            <CloseIcon className="icon icon-sm" />
                        </span>
                    </span>
                ) : null}
                {showHintTooltip ? (
                    <span
                        className={`tooltip ${isDesktopWidth() &&
                            'tooltip-southwest'}`}
                        onClick={() => this.handleCloseTooltip()}>
                        <BulbIcon className="tooltip_icon icon" />
                        <span className="tooltip_content">
                            {STRINGS.DISCOVER.SAVE_SEARCH_TOOLTIP}
                        </span>
                        <span className="tooltip_close-icon">
                            <CloseIcon className="icon icon-sm" />
                        </span>
                    </span>
                ) : null}
            </div>
        );
    }
}

export default SaveSearchButton;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/search-results/save-search-button.jsx